import { useState, Fragment, useEffect } from "react";
import { Link } from "wouter";
import { useGetJobs } from "@workspace/api-client-react";
import { GoogleFeedAd } from "@/components/GoogleAd";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { useTown } from "@/hooks/use-town";
import { useCategories } from "@/hooks/use-categories";
import { timeAgo } from "@/lib/time-ago";
import { Clock } from "lucide-react";
import { useAnalytics } from "@/hooks/use-analytics";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
function quoteTimeRemaining(createdAt: string): { label: string; colour: string } {
  const remaining = new Date(createdAt).getTime() + SEVEN_DAYS_MS - Date.now();
  if (remaining <= 0) return { label: "Expired", colour: "text-slate-400" };
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((remaining % (60 * 60 * 1000)) / 60000);
  const label = days > 0 ? `${days}d ${hours}h left` : hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;
  const colour = days >= 3 ? "text-emerald-600" : days >= 1 ? "text-amber-600" : "text-red-500";
  return { label, colour };
}
import { SidebarFilter } from "@/components/SidebarFilter";

const URGENCY_TABS = [
  { label: "All Quotes", value: "all" },
  { label: "🚨 Emergency", value: "emergency" },
  { label: "This Week", value: "this_week" },
  { label: "Flexible", value: "flexible" },
];

const URGENCY_CONFIG: Record<string, { label: string; colour: string; dot: string }> = {
  emergency:  { label: "Emergency",   colour: "bg-red-100 text-red-700 border-red-200",         dot: "bg-red-500"    },
  within_24h: { label: "Within 24h",  colour: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  this_week:  { label: "This week",   colour: "bg-amber-100 text-amber-700 border-amber-200",    dot: "bg-amber-500"  },
  this_month: { label: "This month",  colour: "bg-yellow-100 text-yellow-700 border-yellow-200", dot: "bg-yellow-400" },
  flexible:   { label: "Flexible",    colour: "bg-green-100 text-green-700 border-green-200",    dot: "bg-green-500"  },
};

export default function Jobs() {
  useSEO({ title: "Get Quotes from Ayrshire Tradespeople", description: "Request quotes from local tradespeople in Ayrshire — plumbers, electricians, builders, joiners and more. Post your request and connect with vetted professionals." });
  const { track } = useAnalytics();
  useEffect(() => { track("page_viewed", { screen: "jobs" }); }, []);
  const { townParam } = useTown();
  const { user } = useAuth();
  const [category, setCategory] = useState("All");
  const [urgency, setUrgency] = useState("all");

  const { data, isLoading } = useGetJobs({
    town: townParam,
    category: category === "All" ? undefined : category,
    urgency: urgency === "all" ? undefined : urgency,
  });
  const jobs = data?.items ?? [];
  const categories = ["All", ...useCategories("job")];

  return (
    <div className="min-h-screen bg-slate-50">

      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Find local tradespeople</p>
              <h1 className="text-2xl font-bold text-primary">Local Quotes</h1>
            </div>
            <Link href="/post-job">
              <Button size="sm" className="bg-primary hover:bg-primary/90">
                {user ? "Request a Quote" : "Get Quotes"}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {(!user || user.role === "resident") && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
              🔧 <strong>Are you a tradesperson?</strong> Join free to browse paid job leads from local residents.
            </p>
            <Link href={user ? "/become-tradesperson" : "/signup?role=tradesperson"}>
              <button className="flex-shrink-0 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-full transition-colors">
                Join as a Tradesperson →
              </button>
            </Link>
          </div>
        </div>
      )}

      <SidebarFilter items={categories} selected={category} onSelect={setCategory}>
        {/* Urgency pills stay inside content area */}
        <div className="border-b border-slate-200 bg-white">
          <div className="flex gap-2 overflow-x-auto px-4 py-2.5" style={{ scrollbarWidth: "none" }}>
            {URGENCY_TABS.map(tab => (
              <button key={tab.value} onClick={() => setUrgency(tab.value)} className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab.value === urgency ? "bg-primary text-white border-primary" : "bg-white text-slate-500 border-slate-200 hover:border-primary hover:text-primary"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 py-5 pb-5">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
                <div className="flex justify-between mb-3">
                  <div className="h-5 bg-slate-200 rounded w-2/3" />
                  <div className="h-5 bg-slate-200 rounded w-16" />
                </div>
                <div className="h-3 bg-slate-200 rounded w-1/4 mb-3" />
                <div className="h-4 bg-slate-200 rounded mb-2" />
                <div className="h-4 bg-slate-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-5xl mb-4">🔧</p>
            <p className="text-slate-500 font-medium">No quote requests yet.</p>
            <p className="text-slate-400 text-sm mt-1">Be the first to request a quote from a local tradesperson.</p>
            <Link href="/post-job" className="mt-4 inline-block">
              <Button className="bg-primary hover:bg-primary/90">Get Quotes Now</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {jobs.map((job, i) => {
              const urgency = URGENCY_CONFIG[job.urgency ?? ""] ?? null;
              return (
                <Fragment key={job.id}>
                <Link
                  href={`/jobs/${job.id}`}
                  className="group block bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-bold text-slate-800 leading-snug group-hover:text-primary transition-colors">{job.title}</h3>
                    {urgency && (
                      <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${urgency.colour}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${urgency.dot}`} />
                        {urgency.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-3">
                    <span>📍</span>
                    <span>{job.town}</span>
                    {job.category && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="capitalize">{job.category}</span>
                      </>
                    )}
                  </div>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">{job.description}</p>
                  <div className="flex items-center justify-between">
                    {job.budgetRange
                      ? <span className="text-sm font-bold text-primary">{job.budgetRange}</span>
                      : <span />
                    }
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-xs text-slate-400">{timeAgo(job.createdAt)}</span>
                      {job.createdAt && (() => { const t = quoteTimeRemaining(job.createdAt); return (
                        <span className={`text-[10px] font-semibold flex items-center gap-1 ${t.colour}`}>
                          <Clock size={9} /> {t.label}
                        </span>
                      ); })()}
                    </div>
                  </div>
                </Link>
                {i === 2 && <div className="col-span-full"><GoogleFeedAd /></div>}
                </Fragment>
              );
            })}
          </div>
        )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="bg-gradient-to-br from-primary/5 to-secondary/10 border border-primary/15 rounded-2xl p-6 text-center">
              <p className="text-lg font-bold text-slate-800 mb-1">Need something done?</p>
              <p className="text-slate-500 text-sm mb-4">Request a quote and local tradespeople will get in touch — usually within hours.</p>
              <Link href="/post-job">
                <Button className="bg-primary hover:bg-primary/90">Get Free Quotes</Button>
              </Link>
            </div>
            {(!user || user.role === "resident") && (
              <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-2xl p-6 text-center">
                <p className="text-lg font-bold text-slate-800 mb-1">Already a tradesperson?</p>
                <p className="text-slate-500 text-sm mb-4">Register free and start receiving paid job leads from residents in your area.</p>
                <Link href={user ? "/become-tradesperson" : "/signup?role=tradesperson"}>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">Join as a Tradesperson</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </SidebarFilter>
    </div>
  );
}
